import { useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { PageContainer } from "@/layouts/PageContainer";
import { Search } from "@/components/ui/Search";
import { Filter } from "@/components/ui/Filter";
import { DataTable } from "@/components/tables/DataTable";
import { Pagination } from "@/components/ui/Pagination";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { useDebounce } from "@/hooks/useDebounce";
import { useRidesList } from "@/features/rides/useRides";
import { RideDetailDrawer } from "@/features/rides/RideDetailDrawer";
import { formatDateTime, formatINR } from "@/utils/format";
import type { RideRow, RideStatus } from "@/types/domain";

const STATUS_OPTIONS: { label: string; value: RideStatus }[] = [
  { label: "Completed", value: "PAYMENT_COMPLETED" },
  { label: "Rated", value: "RATED" },
  { label: "On trip", value: "TRIP_STARTED" },
  { label: "Cancelled", value: "CANCELLED" },
];

export default function Rides() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [selectedRideId, setSelectedRideId] = useState<string | null>(null);
  const debouncedSearch = useDebounce(search);

  const rides = useRidesList({
    page,
    limit: 20,
    search: debouncedSearch || undefined,
    status: (status || undefined) as RideStatus | undefined,
  });

  const columns = useMemo<ColumnDef<RideRow, unknown>[]>(
    () => [
      { header: "Ride", accessorKey: "code" },
      { header: "Rider", accessorKey: "rider" },
      { header: "Driver", accessorKey: "driver" },
      { header: "Pickup", accessorFn: (r) => r.pickup.name ?? r.pickup.area ?? "—" },
      { header: "Drop", accessorFn: (r) => r.drop.name ?? r.drop.area ?? "—" },
      {
        header: "Fare",
        cell: ({ row }) => (row.original.fare !== undefined ? formatINR(row.original.fare) : "—"),
      },
      {
        header: "Status",
        cell: ({ row }) => (
          <StatusBadge status={row.original.status} label={row.original.statusLabel} alert={row.original.alert} />
        ),
      },
      { header: "Date", accessorFn: (r) => formatDateTime(r.createdAt) },
    ],
    [],
  );

  return (
    <PageContainer
      title="Rides"
      description="Full ride history across the platform."
      actions={
        <>
          <Search value={search} onChange={setSearch} placeholder="Search ride code, rider…" />
          <Filter label="Status" value={status} options={STATUS_OPTIONS} onChange={setStatus} />
        </>
      }
    >
      <div className="card">
        <DataTable
          columns={columns}
          data={rides.data?.items ?? []}
          isLoading={rides.isLoading}
          error={rides.error}
          onRetry={() => rides.refetch()}
          onRowClick={(r) => setSelectedRideId(r.id)}
          rowKey={(r) => r.id}
          emptyTitle="No rides found"
        />
        {rides.data && (
          <Pagination page={page} pageSize={20} total={rides.data.total} onPageChange={setPage} />
        )}
      </div>

      <RideDetailDrawer rideId={selectedRideId} onClose={() => setSelectedRideId(null)} />
    </PageContainer>
  );
}
